import { Feature, Map as OlMap, View } from 'ol';
import { defaults } from 'ol/control';
import BaseEvent from 'ol/events/Event';
import { GeoJSON } from 'ol/format';
import { Geometry, Polygon } from 'ol/geom';
import { Interaction, Snap } from 'ol/interaction';
import { SelectEvent } from 'ol/interaction/Select';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import 'ol/ol.css';
import { Projection } from 'ol/proj';
import Static from 'ol/source/ImageStatic';
import VectorSource from 'ol/source/Vector';
import { Fill, Icon, Stroke, Style } from 'ol/style';

import spacePin from '@/assets/icons/space-pin.svg';
import { AbstractInteraction } from '@/shared/lib/openlayers/interaction/AbstractInteraction';
import SelectInteraction from '@/shared/lib/openlayers/interaction/SelectInteraction';
import TickEvent from '@/shared/lib/openlayers/interaction/event/TickEvent';

export class BackgroundImage {
  constructor(
    public url: string,
    public extent: number[],
  ) {}
}

export default class MapController {
  static STYLE = new Style({
    fill: new Fill({ color: 'rgba(111, 63, 194,0.16)' }),
    stroke: new Stroke({ color: '#6F3FC2', width: 1 }),
    image: new Icon({ src: spacePin, width: 25, height: 25 }),
  });
  public geojson: GeoJSON = new GeoJSON();
  private projection: Projection;
  private mapInstance: OlMap;
  private imageSource: Static;
  private imageLayer: ImageLayer<Static>;

  public vectorSource: VectorSource = new VectorSource<Feature<Geometry>>();
  public vectorLayer: VectorLayer = new VectorLayer({
    source: this.vectorSource,
    style: MapController.STYLE,
  });
  private currentInteractions: Interaction[] = [];
  public selected: string[] = [];
  public imageCache = new Map<string, HTMLImageElement>();

  constructor(target: string, initialImage: BackgroundImage) {
    this.projection = new Projection({
      code: 'custom-image',
      units: 'pixels',
      extent: initialImage.extent,
    });

    this.imageSource = new Static({
      url: initialImage.url,
      projection: this.projection,
      imageExtent: initialImage.extent,
    });

    this.imageLayer = new ImageLayer({ source: this.imageSource });

    this.mapInstance = new OlMap({
      target,
      layers: [this.imageLayer, this.vectorLayer],
      view: new View({
        extent: initialImage.extent,
        showFullExtent: true,
      }),
      controls: defaults({ zoom: false }),
    });

    this.mapInstance.getView().fit(initialImage.extent);

    let zoom = this.mapInstance.getView().getZoom();

    this.mapInstance.on('moveend', () => {
      const newZoom = this.mapInstance.getView().getZoom();
      if (newZoom !== zoom) {
        zoom = newZoom;
        this.mapInstance.dispatchEvent({ type: 'zoomed' } as any);
      }
    });

    // @ts-ignore
    this.mapInstance.on('select', (event: SelectEvent) => {
      event.deselected.forEach((feature) => {
        this.selected.splice(this.selected.indexOf(feature.getId() as string), 1);
      });
      this.selected.push(...event.selected.map((feature) => feature.getId() as string));
      this.mapInstance.dispatchEvent({ ...event, type: 'select:external' } as any);
    });
  }

  preloadImage(url: string) {
    if (this.imageCache.has(url)) return;
    const img = new Image();
    this.imageCache.set(url, img);
    img.onload = () => {
      this.vectorLayer.changed();
    };
    img.src = url;
  }

  setupFeatureImageRenderer() {
    this.vectorLayer.setStyle((feature) => {
      const imageUrl = feature.get('imageUrl');

      if (!imageUrl) {
        return MapController.STYLE;
      }

      return new Style({
        stroke: new Stroke({ color: '#6F3FC2', width: 1 }),
        renderer: (coordinates, state) => {
          const context = state.context;

          let image = this.imageCache.get(imageUrl);

          if (!image) {
            image = new Image();
            image.src = imageUrl;
            this.imageCache.set(imageUrl, image);

            image.onload = () => {
              this.vectorLayer.changed();
            };
            return;
          }

          if (!image.complete) return;

          context.save();
          context.beginPath();

          (coordinates as number[][][]).forEach((ring: number[][]) => {
            ring.forEach((coord: number[], i: number) => {
              if (i === 0) context.moveTo(coord[0], coord[1]);
              else context.lineTo(coord[0], coord[1]);
            });
          });

          context.clip();

          const flatCoords = (coordinates as number[][][])[0];
          const xs = flatCoords.map((c: number[]) => c[0]);
          const ys = flatCoords.map((c: number[]) => c[1]);
          const minX = Math.min(...xs);
          const minY = Math.min(...ys);
          const maxX = Math.max(...xs);
          const maxY = Math.max(...ys);

          context.drawImage(image, minX, minY, maxX - minX, maxY - minY);

          context.restore();

          context.beginPath();
          (coordinates as number[][][]).forEach((ring: number[][]) => {
            ring.forEach((coord: number[], i: number) => {
              if (i === 0) context.moveTo(coord[0], coord[1]);
              else context.lineTo(coord[0], coord[1]);
            });
          });
          context.strokeStyle = '#6F3FC2';
          context.lineWidth = 1;
          context.stroke();
        },
      });
    });
  }

  addFeature(feature: any) {
    this.vectorSource.addFeatures(
      this.geojson.readFeatures({ type: 'FeatureCollection', features: [feature] }),
    );
  }

  deleteFeature(feature: Feature<Geometry>) {
    this.vectorSource.removeFeature(feature);
  }

  getAllFeatures(): any[] {
    return this.vectorSource.getFeatures().map((f) => JSON.parse(this.geojson.writeFeature(f)));
  }

  getFeatureById(id: string): Feature<Geometry> | null {
    return this.vectorSource.getFeatureById(id) as Feature<Geometry> | null;
  }

  getFeatureAtPixel(pixel: number[]): Feature<Geometry> | undefined {
    let found: Feature<Geometry> | undefined;
    this.mapInstance.forEachFeatureAtPixel(
      pixel,
      (f) => {
        found = f as Feature<Geometry>;
        return true;
      },
      { layerFilter: (layer) => layer === this.vectorLayer },
    );
    return found;
  }

  assignImageToFeatureAtPixel(pixel: number[], attachmentId: string, imageUrl: string): boolean {
    const feature = this.getFeatureAtPixel(pixel);
    if (!feature) return false;
    feature.set('attachmentId', attachmentId);
    feature.set('imageUrl', imageUrl);
    this.vectorLayer.changed();
    return true;
  }

  cloneFeature(feature: Feature<Geometry>) {
    const cloned = feature.clone();
    cloned.setId(crypto.randomUUID());
    cloned.set('attachmentId', undefined);
    cloned.set('imageUrl', undefined);
    (cloned.getGeometry() as any).translate(30, -30);
    this.vectorSource.addFeature(cloned);
  }

  getFeatureDimensions(feature: Feature<Geometry>): { widthPx: number; heightPx: number } {
    const [minX, minY, maxX, maxY] = feature.getGeometry()!.getExtent();
    return { widthPx: maxX - minX, heightPx: maxY - minY };
  }

  setFeatureDimensions(feature: Feature<Geometry>, widthPx: number, heightPx: number) {
    const [minX, minY] = feature.getGeometry()!.getExtent();
    const coords = [
      [
        [minX, minY],
        [minX + widthPx, minY],
        [minX + widthPx, minY + heightPx],
        [minX, minY + heightPx],
        [minX, minY],
      ],
    ];
    (feature.getGeometry() as Polygon).setCoordinates(coords);
    this.vectorLayer.changed();
  }

  dispatchEvent(event: BaseEvent) {
    this.mapInstance.dispatchEvent(event);
  }

  centerOnFeature() {
    const feature = this.vectorSource
      .getFeatures()
      .filter((feature) => this.selected.indexOf(feature.getId() as string) > -1)?.[0];

    if (feature) {
      const geometry = feature.getGeometry();
      if (geometry) {
        this.mapInstance.getView().fit(geometry.getExtent(), {
          duration: 500,
          maxZoom: this.mapInstance.getView().getZoom(),
        });
      }
    }
  }

  setBackground(image: BackgroundImage): void {
    this.projection = new Projection({
      code: 'custom-image',
      units: 'pixels',
      extent: image.extent,
    });

    this.imageSource = new Static({
      url: image.url,
      projection: this.projection,
      imageExtent: image.extent,
    });

    this.imageLayer.setSource(this.imageSource);

    const view = new View({
      extent: image.extent,
      showFullExtent: true,
    });

    this.mapInstance.setView(view);
    view.fit(image.extent, { size: this.mapInstance.getSize() });
  }

  setInteraction<T extends AbstractInteraction>(
    //@ts-ignore
    interaction: typeof T,
    extraArgs: any = {},
  ): T {
    const interactionExtraOpts = interaction.getExtraOpts();
    this.currentInteractions.forEach((interactionInstance) =>
      this.mapInstance.removeInteraction(interactionInstance),
    );

    if (interactionExtraOpts.hasSelection) {
      const selected = this.vectorSource
        .getFeatures()
        .filter((feature) => this.selected.indexOf(feature.getId() as string) > -1);

      if (interactionExtraOpts.selectionRequired && selected.length !== 1) {
        throw new Error('Invalid selection');
      }

      extraArgs['selected'] = selected[0];
    }

    // @ts-ignore
    const interactionInstance = new interaction(extraArgs, this);
    this.mapInstance.addInteraction(interactionInstance);
    this.currentInteractions = [interactionInstance];
    if (interactionExtraOpts.hasSnap) {
      const snapInteraction = new Snap({ source: this.vectorSource });
      this.mapInstance.addInteraction(snapInteraction);
      this.currentInteractions.push(snapInteraction);
    }

    this.mapInstance.dispatchEvent({
      type: 'interaction:change',
      params: { identifier: interaction.getIdentifier() },
    } as any);

    return interactionInstance;
  }

  onInteractionChange(callback: (event: { params: { identifier: string } }) => void) {
    this.mapInstance.on('interaction:change' as any, callback as any);
  }

  getInteractionId() {
    return (this.currentInteractions[0].constructor as typeof AbstractInteraction).getIdentifier();
  }

  getGeometries() {
    return this.vectorSource.getFeatures().map((feature) => feature.getGeometry());
  }

  setGeometries(geometries: any[]) {
    this.vectorSource.removeFeatures(this.vectorSource.getFeatures());
    this.vectorSource.addFeatures(
      this.geojson.readFeatures({ type: 'FeatureCollection', features: geometries }),
    );
  }

  onSelect(callback: (event: SelectEvent) => void) {
    // @ts-ignore
    this.mapInstance.on('select:external', callback);
  }

  setSelectedFeature(feature: any) {
    if (feature) {
      this.selected = [feature.id];
    }
  }

  deselectAll() {
    this.selected = [];
    this.setInteraction(SelectInteraction, { selected: undefined });
  }

  onSelectedChange(callback: (event: TickEvent) => void) {
    // @ts-ignore
    this.mapInstance.on('tick', callback);
  }

  setZoom(zoom: number) {
    this.mapInstance.getView().setZoom(zoom);
  }

  getZoom(): number | undefined {
    return this.mapInstance.getView().getZoom();
  }

  getMinZoom(): number {
    return this.mapInstance.getView().getMinZoom();
  }

  onZoom(callback: (zoom: number) => void) {
    // @ts-ignore
    this.mapInstance.on('zoomed', () => {
      callback(this.mapInstance.getView().getZoom() ?? 1);
    });
  }
}
